import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { fetchMyCompanies } from '../api/companies';
import { isStandalonePublicRoute } from '../access/publicRoutes';
import {
  readStoredActiveCompanyId,
  writeStoredActiveCompanyId,
} from '../access/companyStorage';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const location = useLocation();
  const isPublicStandalone = isStandalonePublicRoute(location.pathname);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyIdState] = useState(() =>
    readStoredActiveCompanyId(),
  );
  const [noAssignment, setNoAssignment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMyCompanies();
      const list = res?.data?.companies || [];
      setCompanies(list);
      setNoAssignment(res?.data?.noAssignment === true);
      const stored = readStoredActiveCompanyId();
      const ids = list.map((c) => String(c._id || c.id));
      let next = stored && ids.includes(stored) ? stored : ids[0] || '';
      if (next !== stored) writeStoredActiveCompanyId(next);
      setActiveCompanyIdState(next);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les entreprises.');
      setCompanies([]);
      setNoAssignment(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPublicStandalone) {
      setLoading(false);
      return;
    }
    load();
  }, [load, isPublicStandalone]);

  const setActiveCompanyId = useCallback((id) => {
    const next = String(id || '');
    writeStoredActiveCompanyId(next);
    setActiveCompanyIdState(next);
  }, []);

  const value = useMemo(
    () => ({
      companies,
      activeCompanyId,
      setActiveCompanyId,
      loading,
      error,
      noAssignment,
      refetch: load,
    }),
    [
      companies,
      activeCompanyId,
      setActiveCompanyId,
      loading,
      error,
      noAssignment,
      load,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

export function useCompaniesOptional() {
  return useContext(CompanyContext);
}

export function useCompanies() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error('useCompanies must be used within CompanyProvider');
  }
  return ctx;
}
